import React, { useState, useEffect } from 'react';

const DEFAULTS = ['player_id', 'phone', 'country', 'email'];

const ExportDialog = ({ segment, library, onClose }) => {
  const groups = library?.exportGroups || [];
  const segmentGeos = segment.geos || (segment.geo ? [segment.geo] : []);
  const [selected, setSelected] = useState(DEFAULTS);
  // По умолчанию выгружаем весь сегмент; страны нужны, когда он multi-GEO.
  const [geos, setGeos] = useState(segmentGeos);
  const [exports, setExports] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    let timer;
    const refresh = async () => {
      try {
        const res = await fetch(`/segment_exports?segment_id=${segment.id}`);
        if (!res.ok) throw new Error('Could not load exports.');
        const data = await res.json();
        if (active) setExports(data);
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) timer = setTimeout(refresh, 3000);
      }
    };
    refresh();
    return () => { active = false; clearTimeout(timer); };
  }, [segment.id]);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const toggleGeo = (geo) =>
    setGeos((prev) => (prev.includes(geo) ? prev.filter((v) => v !== geo) : [...prev, geo]));

  // Пустой список стран выгрузил бы пустой файл — такой запрос не отправляем.
  const canDownload = selected.length > 0 && (segmentGeos.length <= 1 || geos.length > 0);

  const download = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`/segments/${segment.id}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content },
        body: JSON.stringify({ attributes: selected.join(','), geos: geos.join(',') })
      });
      if (!res.ok) throw new Error('Could not queue export. Try again.');
      const data = await res.json();
      setExports((previous) => [data, ...previous.filter((item) => item.id !== data.id)]);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='space-y-4'>
      <p className='text-xs text-slate-500'>
        Pick the player data you need. CSV is prepared in the background; files are available for 7 days.
      </p>

      {segmentGeos.length > 1 && (
        <div>
          <p className='text-xs font-semibold text-slate-600 mb-1'>Countries</p>
          <div className='flex flex-wrap gap-x-4 gap-y-1'>
            {segmentGeos.map((geo) => (
              <label key={geo} className='flex items-center gap-2 text-sm text-slate-700'>
                <input
                  type='checkbox'
                  className='h-4 w-4 rounded border-slate-300 text-blue-600'
                  checked={geos.includes(geo)}
                  onChange={() => toggleGeo(geo)}
                />
                {geo}
              </label>
            ))}
          </div>
          {geos.length === 0 && (
            <p className='text-xs text-amber-700 mt-1'>Pick at least one country.</p>
          )}
        </div>
      )}

      <div className='max-h-72 overflow-y-auto space-y-3 pr-1'>
        {groups.map((group) => (
          <div key={group.group}>
            <p className='text-xs font-semibold text-slate-600 mb-1'>{group.label}</p>
            <div className='grid grid-cols-2 gap-1'>
              {group.attributes.map((attribute) => (
                <label key={attribute.id} className='flex items-center gap-2 text-sm text-slate-700'>
                  <input
                    type='checkbox'
                    className='h-4 w-4 rounded border-slate-300 text-blue-600'
                    checked={selected.includes(attribute.id)}
                    onChange={() => toggle(attribute.id)}
                  />
                  {attribute.label}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {error && <p role='alert' className='text-sm text-red-600'>{error}</p>}
      {exports.length > 0 && (
        <div className='border-t border-slate-200 pt-3 space-y-2 max-h-48 overflow-y-auto'>
          <p className='text-xs font-semibold text-slate-600'>Recent exports</p>
          {exports.map((item) => (
            <div key={item.id} className='flex items-center justify-between gap-3 text-xs'>
              <span className='text-slate-500'>{new Date(item.created_at).toLocaleString()}</span>
              {item.status === 'ready'
                ? <a className='text-blue-600 hover:underline' href={item.download_url}>Download CSV · {item.rows_count} players</a>
                : <span className={item.status === 'failed' ? 'text-red-600' : 'text-amber-700'}>
                    {item.status === 'queued' ? 'Queued'
                      : item.status === 'failed' ? 'Export failed · prepare a new CSV'
                      : item.status === 'retrying' ? 'Retrying…'
                      : `Preparing · ${item.rows_count} players`}
                  </span>}
            </div>
          ))}
        </div>
      )}

      <div className='flex justify-between items-center'>
        <button
          type='button'
          className='text-xs text-slate-500 hover:text-slate-700'
          onClick={() => setSelected(DEFAULTS)}
        >
          Reset to default columns
        </button>
        <div className='flex gap-2'>
          <button
            type='button'
            className='px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded'
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type='button'
            className={`px-3 py-1.5 text-sm rounded text-white ${
              canDownload && !busy ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-300 cursor-not-allowed'
            }`}
            disabled={!canDownload || busy}
            onClick={download}
          >
            {busy ? 'Queuing…' : 'Prepare CSV'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDialog;
